import React, { useState, useEffect } from 'react'
import { BaseModal } from '../../../../components/Modal/BaseModal'
import type { BaseModalProps } from '../../../../components/Modal/BaseModal'
import { Button } from '../../../../components/Button'
import { Textarea } from '../../../../components/Textarea'
import { HiCheckBadge } from 'react-icons/hi2'
import { StarRating } from '@/components/StarRating/starrating'

export interface FinalizeDemandModalProps extends Omit<BaseModalProps, 'children'> {
  onConfirm: (data: { comentario: string; nota: number }) => void
  onCancel?: () => void
  loading?: boolean
  disabled?: boolean
}

export const FinalizeDemandModal: React.FC<FinalizeDemandModalProps> = ({
  title = 'Finalizar Demanda',
  onConfirm,
  onCancel,
  loading = false,
  disabled = false,
  isOpen,
  onClose,
  ...props
}) => {
  const [comentario, setComentario] = useState('')
  const [isDefaultText, setIsDefaultText] = useState(true)
  const [nota, setNota] = useState(5)

  // Texto padrão para finalização
  const defaultText =
    '<p><strong>Finalização aprovada:</strong></p><p>• Solução implementada conforme solicitado</p><p>• Requisitos atendidos satisfatoriamente</p><p>• Demanda concluída com sucesso</p><p><br></p><p><em>Observações finais:</em></p>'

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setComentario(defaultText)
      setIsDefaultText(true)
      setNota(5) 
    }
  }, [isOpen])

  const handleCancel = () => {
    setComentario('')
    setIsDefaultText(true)
    setNota(5) 
    if (onCancel) {
      onCancel()
    }
    onClose()
  }

  const handleConfirm = () => {
    // Comentário é opcional para finalização
    const finalComment = comentario.trim()
    onConfirm({
      comentario: finalComment,
      nota,
    })
  }

  const handleTextChange = (value: string) => {
    setComentario(value)
    // Se o usuário começou a editar, não é mais texto padrão
    if (isDefaultText && value !== defaultText) {
      setIsDefaultText(false)
    }
  }

  const handleTextFocus = () => {
    // Se ainda é texto padrão, limpar para permitir edição
    if (isDefaultText) {
      setComentario('')
      setIsDefaultText(false)
    }
  }

  const getIcon = () => {
    return <HiCheckBadge className='w-8 h-8 text-blue-500' />
  }

  const getDescription = () => {
    return 'Confirme a finalização da demanda. A solução implementada será aceita e a demanda será marcada como concluída.'
  }

  const footerActions = (
    <div className='flex items-center justify-end space-x-3 p-4'>
      <Button
        variant='outline'
        size='sm'
        onClick={handleCancel}
        disabled={loading}
        className='min-w-20'
      >
        Cancelar
      </Button>

      <Button
        variant='primary'
        size='sm'
        onClick={handleConfirm}
        disabled={disabled}
        isLoading={loading}
        className='min-w-32 bg-blue-600 hover:bg-blue-700'
      >
        Finalizar Demanda
      </Button>
    </div>
  )

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={title}
      description={getDescription()}
      icon={getIcon()}
      useStandardLayout={true}
      size='xl'
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
      resizable={true}
      retractable={true}
      stickyFooter={true}
      footerContent={footerActions}
      {...props}
    >
      <div className='p-4'>
        {/* Seção de Exibição de Qualidade Esperada */}
        <div className='flex items-center justify-center py-2'>
          <StarRating
            totalStars={5}
            defaultValue={nota}
            size='lg'
            disabled={false}
            onRate={setNota}
            className='opacity-100'
          />
        </div>
        <p className='text-xs text-center text-green-700 dark:text-green-300 mt-2'>
          Certifique-se de que a solução atende aos mais altos padrões de qualidade
        </p>
        {/* Message */}
        <div className='text-center mb-4'>
          <p className='text-sm text-gray-900 dark:text-gray-100 leading-relaxed'>
            Você está finalizando esta demanda. Isso significa que a solução implementada está
            completa e atende aos requisitos.
          </p>
        </div>

        {/* Rich Text Editor para comentário */}
        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            Comentário sobre a finalização <span className='text-gray-500'>(opcional)</span>
          </label>
          <Textarea
            variant='rich'
            value={comentario}
            onChange={handleTextChange}
            onFocus={handleTextFocus}
            placeholder='Adicione comentários sobre a finalização, observações finais, ou resultados obtidos...'
            disabled={loading || disabled}
            rows={8}
            className='w-full'
            label=''
            description=''
          />
          <div className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
            {isDefaultText
              ? 'Texto padrão - clique para personalizar'
              : comentario.length > 0
                ? `${comentario.length} caracteres`
                : 'Campo opcional'}
          </div>
        </div>

        {/* Confirmação visual */}
        <div className='mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
          <div className='flex items-start space-x-2'>
            <div className='flex-shrink-0 mt-0.5'>
              <HiCheckBadge className='w-5 h-5 text-green-500' />
            </div>
            <div className='text-xs text-gray-600 dark:text-gray-400'>
              Ao confirmar, você está atestando que a demanda foi completamente resolvida e todos os
              requisitos foram atendidos satisfatoriamente.
            </div>
          </div>
        </div>
      </div>
    </BaseModal>
  )
}